import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { apiPost } from '@/lib/apiFetch';
import { GROQ_MODEL, jsonSchemaFormat } from '@/lib/groq';

// ── Groq RRP suggestion with market context (proxied via /api/groq) ──────────

const XPERT_MARKET_REF = [
  'Intel BLAZE Spark Gaming PC - €1,070 (xpert.mt, entry-level)',
  'AMD VORTEX Ignite Gaming PC - €1,410 (xpert.mt, entry-level)',
  'Intel BLAZE Surge Gaming PC - €1,950 (xpert.mt, mid-range)',
  'AMD VORTEX Proton Gaming PC - €2,190 (xpert.mt, mid-range)',
  'AMD VORTEX Inferno Gaming PC - €2,660 (xpert.mt, high-end)',
  'Intel BLAZE Neutron Gaming PC - €2,850 (xpert.mt, high-end)',
  'Intel BLAZE Ignite Gaming PC - €3,840 (xpert.mt, extreme)',
  'AMD VORTEX Fury Gaming PC - €3,920 (xpert.mt, extreme)',
];

const VAT_RATE = 0.18;
const DEFAULT_MARKUP = 25;

async function suggestRrp(components: { partNumber: string; description: string; category: string; cost: number }[]): Promise<{
  suggestedRrp: number;
  reasoning: string;
  comparables: string[];
}> {
  const compList = components
    .map(c => `- [${c.category}] ${c.partNumber}: ${c.description} (cost: €${c.cost.toFixed(2)})`)
    .join('\n');

  const totalCost = components.reduce((sum, c) => sum + c.cost, 0);

  const data = await apiPost<any>('/api/groq', {
      model: GROQ_MODEL,
      messages: [
        {
          role: 'system',
          content: `You are a PC pricing expert for the Maltese and Southern European market. You will be given a list of PC components with their individual costs, plus reference pricing from local Maltese retailers.

Your job is to estimate a competitive retail price (RRP) in EUR including 18% VAT for a pre-built custom PC using these exact components.

Market context — use these sources for comparable pricing:
- scanmalta.com (Malta's largest electronics retailer)
- xpert.mt (Maltese PC specialist — see reference prices below)
- amazon.it (Italian Amazon, commonly used by Maltese buyers)
- amazon.de (German Amazon, good for EU price benchmarking)
- alternate.de, mindfactory.de (German component/prebuilt retailers)
- pccomponentes.com (Spanish retailer with EU shipping)

Important pricing factors:
- Malta is a small island market — retail prices are typically 5-15% higher than mainland Europe
- Pre-built PCs include assembly, cable management, testing, OS install, and warranty
- Typical builder margin is 15-25% markup on component cost before VAT
- The total component cost (ex-VAT) for this build is €${totalCost.toFixed(2)}

Reference: Current xpert.mt gaming PC pricing (for market positioning):
${XPERT_MARKET_REF.map(r => '  - ' + r).join('\n')}

Return ONLY a JSON object with:
- "suggested_rrp": number (EUR including 18% VAT, rounded to nearest 50)
- "reasoning": string (2-3 sentences explaining the price, referencing specific comparable systems and sites)
- "comparables": array of 2-3 strings, each describing a similar pre-built system with approximate EUR price and source (e.g. "MSI Aegis RS 14 with i7-14700KF + RTX 4070 - ~€1,800 on amazon.de")`,
        },
        {
          role: 'user',
          content: `Components for a custom PC build (total component cost ex-VAT: €${totalCost.toFixed(2)}):\n${compList}\n\nSuggest a competitive RRP in EUR for the Maltese market.`,
        },
      ],
      temperature: 0.3,
      // Reasoning tokens count towards the completion, so this needs headroom
      // well beyond the size of the JSON itself.
      max_tokens: 3000,
      response_format: jsonSchemaFormat('rrp_suggestion', {
        type: 'object',
        properties: {
          suggested_rrp: { type: 'number' },
          reasoning: { type: 'string' },
          comparables: { type: 'array', items: { type: 'string' } },
        },
        required: ['suggested_rrp', 'reasoning', 'comparables'],
        additionalProperties: false,
      }),
  });

  const parsed = JSON.parse(data.choices[0].message.content);
  return {
    suggestedRrp: parsed.suggested_rrp,
    reasoning: parsed.reasoning,
    comparables: parsed.comparables || [],
  };
}

// ── Props ────────────────────────────────────────────────────────────────────
interface PricingToolProps {
  totalCost: number;
  rrpValue: string;
  onRrpChange: (rrp: string) => void;
  components: { partNumber: string; description: string; category: string; cost: number }[];
  filledCount: number;
}

export function PricingTool({ totalCost, rrpValue, onRrpChange, components, filledCount }: PricingToolProps) {
  const [markupPct, setMarkupPct] = useState(DEFAULT_MARKUP);
  const [rrpSuggestion, setRrpSuggestion] = useState<{
    suggestedRrp: number;
    reasoning: string;
    comparables: string[];
  } | null>(null);
  const [suggestingRrp, setSuggestingRrp] = useState(false);

  const rawRrp = totalCost * (1 + markupPct / 100) * (1 + VAT_RATE);
  const markupRrp = totalCost > 0 ? Math.round(rawRrp / 10) * 10 - 1 : 0;

  const handleSuggestRrp = async () => {
    if (filledCount < 2) {
      toast.error('Select at least 2 components before requesting a price suggestion');
      return;
    }
    setSuggestingRrp(true);
    setRrpSuggestion(null);
    try {
      const result = await suggestRrp(components);
      setRrpSuggestion(result);
    } catch (e: any) {
      toast.error('Failed to get RRP suggestion: ' + (e.message || 'Unknown error'));
    } finally {
      setSuggestingRrp(false);
    }
  };

  const manualMu = rrpValue && totalCost > 0
    ? ((parseFloat(rrpValue) / (1 + VAT_RATE) - totalCost) / totalCost * 100)
    : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Pricing</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Option 1: Markup-based */}
          <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase">Cost + Markup</span>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">+</span>
                <input
                  type="number"
                  className="w-12 border rounded px-1 py-0.5 text-[11px] text-center bg-background font-mono"
                  min={0}
                  max={100}
                  value={markupPct}
                  onChange={e => setMarkupPct(parseFloat(e.target.value) || 0)}
                />
                <span className="text-[10px] text-muted-foreground">% MU +18% VAT</span>
              </div>
            </div>
            <div className="text-2xl font-bold font-mono">
              {markupRrp > 0 ? formatCurrency(markupRrp) : '---'}
            </div>
            <p className="text-[11px] text-muted-foreground">
              BOM {formatCurrency(totalCost)} + {markupPct}% + 18% VAT = {formatCurrency(rawRrp)} ({formatCurrency(markupRrp)})
            </p>
            {markupRrp > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-xs"
                onClick={() => onRrpChange(String(markupRrp))}
              >
                Use this
              </Button>
            )}
          </div>

          {/* Option 2: AI suggestion */}
          <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase">Market Estimate</span>
              <Sparkles className="h-4 w-4 text-purple-500" />
            </div>
            {rrpSuggestion ? (
              <>
                <div className="text-2xl font-bold font-mono text-purple-700">
                  {formatCurrency(rrpSuggestion.suggestedRrp)}
                </div>
                <p className="text-[11px] text-purple-800">{rrpSuggestion.reasoning}</p>
                {rrpSuggestion.comparables.length > 0 && (
                  <ul className="text-[10px] text-purple-700 list-disc list-inside space-y-0.5">
                    {rrpSuggestion.comparables.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={() => onRrpChange(String(rrpSuggestion.suggestedRrp))}
                >
                  Use this
                </Button>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold font-mono text-muted-foreground">---</div>
                <p className="text-[11px] text-muted-foreground">AI-based estimate from Malta/EU market comparables</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs gap-1.5"
                  disabled={suggestingRrp || filledCount < 2}
                  onClick={handleSuggestRrp}
                >
                  {suggestingRrp ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {suggestingRrp ? 'Analysing...' : 'Get Estimate'}
                </Button>
              </>
            )}
          </div>

          {/* Option 3: Manual */}
          <div className="border rounded-lg p-4 space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase">Manual RRP</span>
            <Input
              type="number"
              step="0.01"
              className="text-2xl font-bold font-mono h-auto py-1 px-2"
              placeholder="0.00"
              value={rrpValue}
              onChange={e => onRrpChange(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              {manualMu !== null
                ? `MU: ${manualMu.toFixed(1)}% over BOM (ex-VAT)`
                : 'Enter your own price inc. VAT'
              }
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
